import { captureException } from "@sentry/react";
import { LoaderOverlay } from "@src/components/loaderOverlay/LoaderOverlay";
import { NavButton } from "@src/components/navButton/NavButton";
import {
  Button,
  FormAction,
  FormField,
  FormGrid,
  FormRow,
} from "@lya-protect/lya-protect-form-library/dist/UI";
import { LinkButton } from "@src/components/linkButton/LinkButton";
import { RecapCard } from "@src/components/recapCard/RecapCard";
import {
  Form,
  InputCheckbox,
  useFormConfig,
} from "@lya-protect/lya-protect-form-library/dist/FormAPI";
import { zod } from "@lya-protect/lya-protect-form-library/dist/Exports";
import { displayToast } from "@src/components/toaster/Toaster";
import { _Activity } from "@src/Steps/simulate/activities/Activities";
import ACTIVITES from "@src/Steps/simulate/activities/activitiesConfig";
import { StepProps } from "@src/Steps/step/Steps";
import "./recap.scss";
import { CONSENT_DEMATERIALISE } from "@src/Steps/subscribe/consents/Consent";
import axios from "axios";
import { useState } from "react";

export type RecapData = undefined;
type RecapProps = StepProps<undefined>;

const schema = zod.object({
  consent: zod
    .boolean({ required_error: "Champ Requis" })
    .refine((val) => val, { message: "Champ requis" }),
});

type FormValues = zod.infer<typeof schema>;

function cleanMaskedInput(val: unknown) {
  if (typeof val !== "string") return undefined;
  return val.replace(/\s/g, "");
}

export function Recap({
  changeStep,
  stepsData,
  setProjetPreSouscription: setProject,
}: RecapProps) {
  const { name, surname } = stepsData.CONTACT_INFOS;
  const formConfig = useFormConfig<typeof schema, FormValues>({
    schema,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  if (!name || !surname) return changeStep("CONTACT_INFOS", "backward");

  type CreateDevisResponse = { value: string };
  const handleSubmit = async () => {
    const consentements = [
      {
        description:
          "J'accepte le stockage et le traitement de mes données personnelles afin de pouvoir recevoir le devis demandé.",
        type: "CONSENTEMENT_TRAITEMENT_DONNEES",
      },
    ];
    setIsSubmitting(true);
    const activityNames = ACTIVITES.filter(
      (activity: _Activity) => stepsData.ACTIVITIES[activity.name]
    ).map((activity: _Activity) => activity.label);

    if (stepsData.ADDRESS_INFOS.consent) {
      consentements.push({
        description: CONSENT_DEMATERIALISE,
        type: "CONSENTEMENT_DEMATERIALISATION",
      });
    }

    if (stepsData.ADDRESS_INFOS.accept) {
      consentements.push({
        description:
          "J'accepte que les services de la mutuelle me rappellent au sujet de mon devis.",
        type: "CONSENTEMENT_CONTACT",
      });
    }

    try {
      await axios.post<CreateDevisResponse>(
        `//${process.env.VITE_URL_GATEWAY}/devis`,
        {
          besoin: {
            activites: activityNames,
            assure: {
              civilite: stepsData.CONTACT_INFOS.civility,
              nom: stepsData.CONTACT_INFOS.name,
              prenom: stepsData.CONTACT_INFOS.surname,
              contactMail: stepsData.ADDRESS_INFOS.email,
              naissanceDate: stepsData.BIRTHDATE.birthDate,
              contactTelephone: cleanMaskedInput(
                stepsData.ADDRESS_INFOS?.mobilePhone
              ),
              ville: stepsData.ADDRESS_INFOS.city,
              voie: stepsData.ADDRESS_INFOS.address,
              codePostal: stepsData.ADDRESS_INFOS.zipcode,
            },
            consentements,
          },
          formuleName: stepsData.TARIF?.name,
          adherent: stepsData.SITUATION?.isMemberTutelaire,
          questions: {
            isMemberTutelaire: stepsData.SITUATION?.isMemberTutelaire,
            hadContractPaxivie: stepsData.SITUATION?.hasContractPavixie,
          },
        }
      );
      setProject({
        contact: {
          birthDate: stepsData.BIRTHDATE.birthDate as Date,
          civility: stepsData.CONTACT_INFOS.civility as "Monsieur" | "Madame",
          mail: stepsData.ADDRESS_INFOS.email,
          name: stepsData.CONTACT_INFOS.name as string,
          surname: stepsData.CONTACT_INFOS.surname as string,
          mobile: cleanMaskedInput(
            stepsData.ADDRESS_INFOS.mobilePhone
          ) as string,
          city: stepsData.ADDRESS_INFOS.city,
          address: stepsData.ADDRESS_INFOS.address,
          zipcode: stepsData.ADDRESS_INFOS.zipcode,
        },
        tarif: {
          name: stepsData.TARIF?.name,
          simulationCotisation: stepsData.TARIF?.simulationCotisation,
        },
        questions: {
          isMemberTutelaire: stepsData.SITUATION?.isMemberTutelaire || false,
        },
      });
      changeStep("CONFIRMATION_SIMULATION", "forward");
    } catch (err) {
      displayToast("Une erreur inconnue est survenue", "error");
      captureException(err);
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <LoaderOverlay isLoading={isSubmitting} />

      <div className="recap-step">
        <div className="stepper__centered-container">
          <div className="stepper__centered-content step__grid step__grid--padding-bottom">
            <NavButton
              onClick={() => {
                changeStep("ADDRESS_INFOS", "backward");
              }}
            >
              Précédent
            </NavButton>
            <Form formConfig={formConfig} onSubmit={handleSubmit}>
              <FormGrid>
                <div className="step__grid-bloc recap-step__recap">
                  <RecapCard
                    fullname={`${surname} ${name}`}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    couvertureName={stepsData.TARIF.name}
                    simulationCotisationMonth={
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      stepsData.TARIF.simulationCotisation
                    }
                  />
                  <div className="step__grid-bloc recap-step__edit-container">
                    <LinkButton onClick={() => changeStep("TARIF", "backward")}>
                      Modifier ma formule
                    </LinkButton>
                    <LinkButton
                      onClick={() => changeStep("CONTACT_INFOS", "backward")}
                    >
                      Modifier mes informations personnelles
                    </LinkButton>
                  </div>
                </div>
                <FormRow>
                  <FormField>
                    <InputCheckbox
                      className="contact-form__checkbox"
                      required
                      name="consent"
                      label="J'accepte le stockage et le traitement de mes données personnelles afin de pouvoir recevoir le devis demandé."
                    />
                  </FormField>
                </FormRow>
                <FormAction>
                  <Button
                    type="submit"
                    disabled={
                      !formConfig.formState.isValid ||
                      formConfig.formState.isSubmitted ||
                      formConfig.formState.isSubmitSuccessful ||
                      isSubmitting
                    }
                  >
                    Recevoir mon devis
                  </Button>
                </FormAction>
              </FormGrid>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
}
